import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
  HostListener,
  ViewChild,
  ChangeDetectorRef,
  OnInit,
  OnDestroy,
  Signal,
  signal,
} from '@angular/core';
import { AnimationEvent } from '@angular/animations';
import { Subscription } from 'rxjs';

import {
  CdkConnectedOverlay,
  STANDARD_DROPDOWN_ADJACENT_POSITIONS,
  type ViewportMargin,
} from '@angular/cdk/overlay';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { PermissionService } from 'src/app/auth/services/permission.service';
import { UserStateService } from 'src/app/auth/services/user-state.service';
import {
  DISPLAY_ACCESS,
  meetsAccessLevel,
  SCHEDULE_ACCESS,
} from 'src/app/auth/types/access-level';
import { ENTITY_TYPES } from 'src/app/auth/types/entity-type';
import { FeaturesService } from 'src/app/components/plans/features.service';
import { RvshareAppDetectionService } from 'src/app/screen-sharing/services/rvshare-app-detection.service';
import { type SidenavState } from '../app/app.component';
import { BroadcasterService } from 'src/app/shared/services/broadcaster.service';
import { StateService } from '@uirouter/core';
import {
  CAP_ALERTS_PAGE_STATE,
  INTEGRATIONS_LIST_STATE,
  INTEGRATIONS_OPEN_STATE,
  INTEGRATIONS_STATE,
} from 'src/app/settings/services/integrations/integrations-company/integrations-company.routing';
import {
  CAP_ALERTS_REQUIRE_ROLE,
  isCapAlertsPageEnabled,
} from 'src/app/settings/services/integrations/models/integrations-company.catalog';

interface NavOption {
  id: string;
  label: string;
  route?: string;
  icon?: string;
  folders?: boolean;
  tooltip?: string;
  addon?: string;
  addonClass?: string;
  states?: string[];
  params?: Record<string, string>;
  clickHandler?: () => void;
  hasPopup?: 'dialog';
}

@Component({
  selector: 'sidebar',
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
  standalone: false,
})
export class SidebarComponent implements OnChanges, OnInit, OnDestroy {
  @Input() state: SidenavState = 'expand';
  @Input() parentAnimating = false;
  @Input() currentRoute: string = '';
  @Input() hideNavigation: boolean = false;
  @Input() isMobile: Signal<boolean>;
  @Output() companyMenuOpened = new EventEmitter<boolean>();
  @Output() toggleSidenav = new EventEmitter<void>();
  @ViewChild(CdkConnectedOverlay) companyPanelOverlay?: CdkConnectedOverlay;

  readonly companyPanelOpen = signal(false);
  readonly companyPanelPositions = STANDARD_DROPDOWN_ADJACENT_POSITIONS;

  animatingItems = false;
  animationState: string = 'expand';
  isCompanyMenuOpen = false;
  private companyPanelNavigated = false;
  showMenu = true;
  titleLogo: 'rise-logo' | 'sidebar' = 'rise-logo';
  showSchedulesNav = false;
  showDisplaysNav = false;
  showScreenSharingNav = false;

  private companyChangeSubscription: Subscription;
  private userChangeSubscription: Subscription;
  private schedulesNavRequestId = 0;
  private displaysNavRequestId = 0;
  protected navOptions: Record<string, NavOption> = {};

  private refreshNavOptions() {
    this.navOptions = {
      presentations: {
        id: 'presentations-link',
        label: 'Presentations',
        route: 'apps.editor.list',
        icon: 'presentations',
        folders: true,
        states: [
          'apps.chat',
          'apps.editor.home',
          'apps.editor.list',
          'apps.editor.workspace.artboard',
          'apps.editor.workspace.htmleditor',
          'apps.editor.templates.edit',
          'apps.editor.create',
        ],
      },
      schedules: {
        id: 'schedules-link',
        label: 'Schedules',
        route: 'apps.schedules.list',
        icon: 'calendar',
        folders: true,
        states: [
          'apps.schedules.home',
          'apps.schedules.list',
          'apps.schedules.details',
          'apps.schedules.add',
        ],
      },
      displays: {
        id: 'displays-link',
        label: 'Displays',
        route: 'apps.displays.list',
        icon: 'display',
        folders: true,
        states: [
          'apps.displays.home',
          'apps.displays.list',
          'apps.displays.add',
          'apps.displays.activate',
          'apps.displays.details',
        ],
      },
      'screen-sharing': {
        id: 'screen-sharing-link',
        label: 'Screen Share',
        tooltip: 'Screen Share',
        route: 'apps.screen-sharing.moderator-join',
        icon: 'screen-share',
        states: [
          'apps.screen-sharing.moderator-join',
          'apps.screen-sharing.moderator-room',
        ],
      },
      'screen-sharing-upgrade': {
        id: 'screen-sharing-upgrade-button',
        label: 'Screen Share',
        icon: 'screen-share',
        addon: 'requires-upgrade',
        addonClass: 'small',
        tooltip: 'Upgrade to access Screen Sharing',
        clickHandler: this.upgradePlan.bind(this),
      },
      storage: {
        id: 'storage-link',
        label: 'Storage',
        route: 'apps.storage.home',
        icon: 'cloud-storage',
        states: ['apps.storage.home'],
      },
      settings: {
        id: 'settings-link',
        label: 'Settings',
        route: 'apps.settings.home',
        icon: 'settings',
        states: [
          'apps.settings.home',
          'apps.settings.company-licenses',
          'apps.settings.company-details',
          'apps.settings.sso-settings',
          'apps.settings.billing.home',
          'apps.settings.user-list',
        ],
      },
      'cap-alerts': isCapAlertsPageEnabled(this.userStateService)
        ? {
            id: 'cap-alerts-link',
            label: 'CAP Alerts',
            route: CAP_ALERTS_PAGE_STATE,
            icon: 'cap-alerts',
            states: [CAP_ALERTS_PAGE_STATE],
          }
        : {
            id: 'cap-alerts-link',
            label: 'CAP Alerts',
            route: INTEGRATIONS_OPEN_STATE,
            params: { integrationId: 'cap-alerts' },
            icon: 'cap-alerts',
            states: [INTEGRATIONS_OPEN_STATE],
            hasPopup: 'dialog',
          },
      'cap-alerts-upgrade': {
        id: 'cap-alerts-upgrade-button',
        label: 'CAP Alerts',
        icon: 'cap-alerts',
        addon: 'requires-upgrade',
        addonClass: 'small',
        tooltip: 'Upgrade to access CAP Alerts',
        hasPopup: 'dialog',
        clickHandler: this.upgradePlan.bind(
          this,
          FeaturesService.FEATURE_ALERTS,
        ),
      },
      integrations: {
        id: 'integrations-link',
        label: 'Integrations',
        route: INTEGRATIONS_LIST_STATE,
        icon: 'integrations',
        states: [
          INTEGRATIONS_STATE,
          INTEGRATIONS_LIST_STATE,
          INTEGRATIONS_OPEN_STATE,
        ],
      },
    };
  }

  constructor(
    protected companyStateService: CompanyStateService,
    protected userStateService: UserStateService,
    protected permissionService: PermissionService,
    protected featuresService: FeaturesService,
    protected rvshareAppDetectionService: RvshareAppDetectionService,
    private broadcasterService: BroadcasterService,
    private stateService: StateService,
    private changeDetector: ChangeDetectorRef,
  ) {}

  isNavSelected(states?: string[], params?: Record<string, string>): boolean {
    if (!states || states.indexOf(this.currentRoute) === -1) {
      return false;
    }

    if (params) {
      return Object.entries(params).every(
        ([key, value]) => this.stateService.params?.[key] === value,
      );
    }

    return !Object.values(this.navOptions).some(
      (option) =>
        !!option.params &&
        !!option.states?.includes(this.currentRoute) &&
        Object.entries(option.params).every(
          ([key, value]) => this.stateService.params?.[key] === value,
        ),
    );
  }

  ngOnInit() {
    this.refreshNavVisibility();

    this.companyChangeSubscription = this.broadcasterService.on(
      'risevision.company.selectedCompanyChanged',
      () => {
        this.refreshNavVisibility();
        setTimeout(() => {
          this.showMenu = false;
          this.changeDetector.detectChanges();
          this.showMenu = true;
          this.changeDetector.detectChanges();
        });
      },
    );

    this.userChangeSubscription = this.broadcasterService.on(
      'risevision.user.updated',
      () => {
        this.refreshNavVisibility();
        this.changeDetector.detectChanges();
      },
    );
  }

  canAccessSettings(): boolean {
    return (
      this.userStateService.hasPermission('sa ua') ||
      this.userStateService.canAccessBilling()
    );
  }

  canAccessCapAlerts(): boolean {
    return this.userStateService.hasPermission(CAP_ALERTS_REQUIRE_ROLE);
  }

  private refreshNavVisibility(): void {
    this.refreshSchedulesNavVisibility();
    this.refreshDisplaysNavVisibility();
  }

  private refreshSchedulesNavVisibility(): void {
    const requestId = ++this.schedulesNavRequestId;

    if (
      this.permissionService.canBrowseAllLibraries() ||
      this.permissionService.hasAccess(SCHEDULE_ACCESS.PLAYLIST_MANAGER)
    ) {
      this.showSchedulesNav = true;
      return;
    }

    this.showSchedulesNav = false;

    this.permissionService
      .canAccessSchedules()
      .then((allowed) => {
        if (requestId !== this.schedulesNavRequestId) {
          return;
        }

        this.showSchedulesNav = allowed;
        this.changeDetector.detectChanges();
      })
      .catch(() => {
        if (requestId !== this.schedulesNavRequestId) {
          return;
        }

        this.showSchedulesNav = false;
        this.changeDetector.detectChanges();
      });
  }

  private refreshDisplaysNavVisibility(): void {
    const requestId = ++this.displaysNavRequestId;

    this.showDisplaysNav = this.permissionService.canBrowseDisplays();
    this.showScreenSharingNav = this.permissionService.canBrowseScreenSharing();

    const probes: Array<Promise<void>> = [];

    if (!this.showDisplaysNav) {
      probes.push(
        this.permissionService
          .loadFirstSharedAccess(ENTITY_TYPES.DISPLAY)
          .then((shareLevel) => {
            if (requestId !== this.displaysNavRequestId || !shareLevel) {
              return;
            }

            if (meetsAccessLevel(shareLevel, DISPLAY_ACCESS.FULL_ACCESS)) {
              this.showDisplaysNav = true;
            }
          })
          .catch(() => undefined),
      );
    }

    if (!this.showScreenSharingNav) {
      probes.push(
        this.permissionService
          .canAccessScreenSharing()
          .then((allowed) => {
            if (requestId !== this.displaysNavRequestId) {
              return;
            }

            if (allowed) {
              this.showScreenSharingNav = true;
            }
          })
          .catch(() => undefined),
      );
    }

    if (!probes.length) {
      return;
    }

    void Promise.all(probes).then(() => {
      if (requestId !== this.displaysNavRequestId) {
        return;
      }

      this.changeDetector.detectChanges();
    });
  }

  ngOnDestroy() {
    this.schedulesNavRequestId++;
    this.displaysNavRequestId++;

    if (this.companyChangeSubscription) {
      this.companyChangeSubscription.unsubscribe();
    }

    if (this.userChangeSubscription) {
      this.userChangeSubscription.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['state']) {
      this.animationState = this.state;
      this.animatingItems = true;

      if (this.isCompanyMenuOpen) {
        this.updateOverlayPosition();
      }
    }
    if (changes['currentRoute']) {
      if (!this.hideNavigation) {
        this.refreshNavOptions();
      }
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    if (this.isCompanyMenuOpen) {
      this.updateOverlayPosition();
    }
  }

  handleMenuOpened() {
    this.isCompanyMenuOpen = true;
    this.companyMenuOpened.emit(true);
  }

  handleMenuClosed() {
    this.isCompanyMenuOpen = false;
    this.companyMenuOpened.emit(false);
  }

  companyPanelViewportMargin(): ViewportMargin {
    if (this.isMobile?.()) {
      return { top: 80, bottom: 80, start: 12, end: 12 };
    }

    return 12;
  }

  openCompanyPanel(): void {
    if (
      this.userStateService.isAssignedPublisher() ||
      this.companyPanelOpen()
    ) {
      return;
    }

    this.companyPanelNavigated = false;
    this.companyPanelOpen.set(true);
    this.handleMenuOpened();
  }

  closeCompanyPanel(): void {
    if (!this.companyPanelOpen()) {
      return;
    }

    this.companyPanelOpen.set(false);
  }

  onCompanyPanelNavigate(): void {
    this.companyPanelNavigated = true;
    this.closeCompanyPanel();
  }

  onCompanyPanelKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' || event.key === 'Esc') {
      event.preventDefault();
      this.closeCompanyPanel();
    }
  }

  onCompanyPanelDetach(): void {
    this.companyPanelOpen.set(false);

    if (this.isCompanyMenuOpen) {
      this.handleMenuClosed();
    }

    const navigated = this.companyPanelNavigated;
    this.companyPanelNavigated = false;

    if (!navigated) {
      document.getElementById('company-menu-button')?.focus();
    }
  }

  private updateOverlayPosition(): void {
    this.companyPanelOverlay?.overlayRef?.updatePosition();
  }

  upgradePlan(feature: string = FeaturesService.FEATURE_SCREEN_SHARING) {
    this.featuresService.showUpgradePlanModal(feature);
  }

  onSidebarAnimationStart(event: AnimationEvent): void {
    if (event.toState === 'expand' || event.toState === 'collapse') {
      this.animatingItems = true;
    }
  }

  onSidebarAnimationDone(): void {
    this.animatingItems = false;
  }

  onLogoClick(): void {
    if (this.state === 'expand' || this.isMobile()) {
      if (this.rvshareAppDetectionService.isScreenShareApp()) {
        this.stateService.go('apps.screen-sharing.home');
      } else {
        this.stateService.go('apps.home');
      }
    } else {
      this.toggleSidenav.emit();
    }
  }
}
